import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, DollarSign, Search, Check, Save,
  Percent, Eye, EyeOff, Package, RefreshCw, LogOut,
  MapPin, ShoppingCart, Users, Plug
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { API, getAuthHeaders } from '../App';

export default function AdminCityPrices({ user, onLogout }) {
  const { cityId, lang } = useParams();
  const navigate = useNavigate();
  const currentLang = lang || 'ru';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [city, setCity] = useState(null);
  const [baseCityName, setBaseCityName] = useState('');
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMarkup, setBulkMarkup] = useState(20);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [changes, setChanges] = useState({});

  useEffect(() => {
    fetchPriceList();
  }, [cityId]);

  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      setFilteredItems(items.filter(item => 
        item.product_name.toLowerCase().includes(query) ||
        item.product_name_ru?.toLowerCase().includes(query)
      ));
    } else {
      setFilteredItems(items);
    }
  }, [searchQuery, items]);

  const fetchPriceList = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const res = await fetch(`${API}/cities/${cityId}/prices`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setCity(data.city);
      setBaseCityName(data.base_city_name);
      setItems(data.items);
      setFilteredItems(data.items);
    } catch (error) {
      console.error('Error fetching price list:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handlePriceChange = (productId, newPrice) => {
    setChanges(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        price: parseFloat(newPrice) || 0,
        is_manual: true
      }
    }));
    
    setItems(prev => prev.map(item => 
      item.product_id === productId 
        ? { ...item, city_price: parseFloat(newPrice) || 0, is_manual: true }
        : item
    ));
  };

  const handleAvailabilityChange = (productId, isAvailable) => {
    setChanges(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        is_available: isAvailable
      }
    }));
    
    setItems(prev => prev.map(item => 
      item.product_id === productId 
        ? { ...item, is_available: isAvailable }
        : item
    ));
  };

  const saveChanges = async () => {
    setSaving(true);
    
    try {
      for (const [productId, change] of Object.entries(changes)) {
        await fetch(`${API}/cities/${cityId}/prices/${productId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify(change)
        });
      }
      
      setChanges({});
      fetchPriceList();
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setSaving(false);
    }
  };

  const applyBulkMarkup = async () => {
    if (selectedIds.size === 0) return;
    
    setSaving(true);
    
    try {
      await fetch(`${API}/cities/${cityId}/prices/bulk-markup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          city_id: cityId,
          product_ids: Array.from(selectedIds),
          markup_percent: bulkMarkup
        })
      });
      
      setShowBulkModal(false);
      setSelectedIds(new Set());
      fetchPriceList();
    } catch (error) {
      console.error('Error applying bulk markup:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredItems.map(item => item.product_id)));
    }
  };

  const toggleSelect = (productId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedIds(newSelected);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const hasChanges = Object.keys(changes).length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}`} className="flex items-center">
                <img 
                  src="/logo.jpg" 
                  alt="Ainanan" 
                  className="h-10 object-contain"
                />
              </Link>
              <div className="hidden md:block">
                <h1 className="text-lg font-semibold text-gray-900">Прайс-лист: {city?.name}</h1>
                <p className="text-xs text-gray-500">Базовая цена: {baseCityName}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => fetchPriceList(true)}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <LanguageSwitcher />
              <Button variant="ghost" size="sm" onClick={onLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Выход
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Back Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-[1600px] mx-auto px-4">
          <div className="flex items-center gap-4 py-3">
            <Button onClick={() => navigate(`/${currentLang}/admin/cities`)} variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              К городам
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-4 py-6">
        {/* Search & Actions */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Поиск товара..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
              
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  Товаров: {filteredItems.length}
                </span>
                
                {selectedIds.size > 0 && (
                  <Button
                    onClick={() => setShowBulkModal(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Percent className="w-4 h-4 mr-2" />
                    Наценка ({selectedIds.size})
                  </Button>
                )}
                
                {hasChanges && (
                  <Button
                    onClick={saveChanges}
                    disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {saving ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Сохранить
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Price List Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === filteredItems.length && filteredItems.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 text-emerald-600 rounded"
                      />
                      <span className="text-xs font-medium text-gray-500 uppercase">Выбрать</span>
                    </label>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Товар
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {baseCityName}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {city?.name}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Наценка
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Доступен
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Тип
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredItems.map((item) => (
                  <tr 
                    key={item.product_id}
                    className={`hover:bg-gray-50 ${!item.is_available ? 'bg-gray-50 opacity-60' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.product_id)}
                        onChange={() => toggleSelect(item.product_id)}
                        className="w-4 h-4 text-emerald-600 rounded"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Package className="w-5 h-5 text-gray-400" />
                        <div>
                          <div className="font-medium text-gray-900">
                            {item.product_name_ru || item.product_name}
                          </div>
                          {item.product_name_ru && item.product_name_ru !== item.product_name && (
                            <div className="text-xs text-gray-500">{item.product_name}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-gray-600">{item.base_price.toLocaleString()} ₸</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <input
                        type="number"
                        value={item.city_price}
                        onChange={(e) => handlePriceChange(item.product_id, e.target.value)}
                        className={`w-28 px-3 py-1 text-right border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                          changes[item.product_id]?.price !== undefined 
                            ? 'border-emerald-400 bg-emerald-50' 
                            : 'border-gray-300'
                        }`}
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.markup_percent !== null && (
                        <span className={`text-sm font-medium ${
                          item.markup_percent > 0 ? 'text-green-600' : 
                          item.markup_percent < 0 ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {item.markup_percent > 0 ? '+' : ''}{item.markup_percent}%
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleAvailabilityChange(item.product_id, !item.is_available)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          item.is_available 
                            ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                            : 'bg-red-100 text-red-600 hover:bg-red-200'
                        }`}
                      >
                        {item.is_available ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        item.is_manual 
                          ? 'bg-purple-100 text-purple-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {item.is_manual ? 'Вручную' : 'Авто'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredItems.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Товары не найдены
            </div>
          )}
        </Card>
      </div>

      {/* Bulk Markup Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Применить наценку</h2>
              <p className="text-sm text-gray-500 mt-1">
                Выбрано товаров: {selectedIds.size}
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Наценка от базовой цены ({baseCityName})
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={bulkMarkup}
                    onChange={(e) => setBulkMarkup(parseFloat(e.target.value) || 0)}
                    className="flex-1 px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    min="0"
                    max="100"
                  />
                  <span className="text-2xl font-bold text-gray-400">%</span>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  Пример: товар за <strong>1000 ₸</strong> станет{' '}
                  <strong>{Math.round(1000 * (1 + bulkMarkup / 100))} ₸</strong>
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowBulkModal(false)}
                >
                  Отмена
                </Button>
                <Button
                  onClick={applyBulkMarkup}
                  disabled={saving}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Применить
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="fixed bottom-6 right-6 bg-emerald-600 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 z-40">
          <span>Есть несохранённые изменения</span>
          <Button
            size="sm"
            variant="secondary"
            onClick={saveChanges}
            disabled={saving}
            className="bg-white text-emerald-600 hover:bg-emerald-50"
          >
            Сохранить
          </Button>
        </div>
      )}
    </div>
  );
}
